import { useCallback, useEffect, useState } from 'react';
import { Repeat, Hand, X, Check } from 'lucide-react';
import { SECTION_COLOR, SECTION_BORDER, hlavicky } from './komunikace';

/**
 * Karta záskoku ve zprávě kanálu (Komunikace, etapa 5).
 *
 * Kolega s volnem a kvalifikací vidí „Beru" / „Nemůžu", vedoucí po prvním
 * „Beru" „Potvrdit" / „Odmítnout". Stav si karta načítá sama; otevřený chat
 * jí po události `ChatObjektZmenen` zvedne `obnova`, takže se změna ukáže
 * všem hned.
 */
const STAV = {
    nabizeno: 'Hledá se',
    ceka_potvrzeni: 'Čeká na potvrzení',
    obsazeno: 'Obsazeno',
    zruseno: 'Zrušeno',
};

export default function ZaskokKarta({ id, obnova = 0, isDark, onZmena }) {
    const [d, setD] = useState(null);
    const [bezi, setBezi] = useState(null);
    const [chyba, setChyba] = useState(null);

    const nacti = useCallback(() => {
        fetch(`/api/zaskoky/${id}`).then(r => (r.ok ? r.json() : null)).then(x => x && setD(x)).catch(() => {});
    }, [id]);
    useEffect(() => { nacti(); }, [nacti, obnova]);

    const akce = async (co) => {
        setBezi(co);
        setChyba(null);
        try {
            const res = await fetch(`/api/zaskoky/${id}/${co}`, { method: 'POST', headers: hlavicky(false) });
            const x = await res.json().catch(() => ({}));
            if (!res.ok) { setChyba(x.error || 'Nepodařilo se.'); nacti(); return; }
            setD(x);
            onZmena?.();
        } catch {
            setChyba('Chyba sítě.');
        } finally {
            setBezi(null);
        }
    };

    const hrana = isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)';
    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const tlac = (co, text, Icon, kladne) => (
        <button type="button" onClick={(e) => { e.stopPropagation(); akce(co); }} disabled={!!bezi} className="flex items-center justify-center gap-1.5 rounded-xl font-bold"
            style={{ flex: 1, height: 44, fontSize: 14, cursor: bezi ? 'wait' : 'pointer', background: kladne ? SECTION_COLOR : 'transparent', color: kladne ? '#fff' : 'var(--text-primary)', border: kladne ? 'none' : `1px solid ${hrana}` }}>
            {bezi === co ? '…' : <><Icon size={16} /> {text}</>}
        </button>
    );
    const radek = (l, h) => (h ? (
        <div className="flex justify-between gap-3" style={{ padding: '7px 0', borderBottom: `1px solid ${cara}`, fontSize: 13 }}>
            <span style={{ color: 'var(--text-muted)' }}>{l}</span><b className="text-right" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{h}</b>
        </div>
    ) : null);

    if (!d) {
        return <div className="rounded-xl text-sm" style={{ border: `1px solid ${SECTION_BORDER}`, padding: '12px 14px', color: 'var(--text-muted)', width: 'min(440px, 100%)' }}>Načítám nabídku směny…</div>;
    }

    const vyrizeno = d.stav === 'obsazeno' || d.stav === 'zruseno';
    return (
        <div className="rounded-xl" onClick={e => e.stopPropagation()}
            style={{ border: `1px solid ${vyrizeno ? hrana : SECTION_BORDER}`, background: isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.02)', padding: '12px 14px', width: 'min(440px, 100%)', opacity: d.stav === 'zruseno' ? 0.7 : 1 }}>
            <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
                <span style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(180,122,234,0.12)', color: SECTION_COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Repeat size={16} /></span>
                <b className="flex-1" style={{ color: 'var(--text-primary)', fontSize: 14.5 }}>{d.muzu_vzit ? 'Nabídka směny pro tebe' : 'Hledá se záskok'}</b>
                <span className="text-[11px] font-semibold rounded-full" style={{ padding: '2px 8px', color: d.stav === 'obsazeno' ? SECTION_COLOR : 'var(--text-secondary)', border: `1px solid ${hrana}` }}>{STAV[d.stav]}</span>
            </div>
            {radek('Směna', d.smena ? `${d.smena.popis}${d.smena.nazev ? ` · ${d.smena.nazev}` : ''}` : null)}
            {radek('Za koho', d.puvodni ? `${d.puvodni}${d.duvod === 'nemoc' ? ' · nemoc' : ''}` : null)}
            {radek(d.stav === 'obsazeno' ? 'Bere' : 'Kandidát', d.kandidat)}
            {d.nemuze?.length > 0 && radek('Nemůže', d.nemuze.length > 3 ? `${d.nemuze.slice(0, 2).join(', ')} +${d.nemuze.length - 2}` : d.nemuze.join(', '))}
            {d.poznamka && <p className="text-[13px]" style={{ color: 'var(--text-secondary)', margin: '8px 0 0' }}>{d.poznamka}</p>}

            {d.muzu_vzit && !d.moje_odpoved && (
                <div className="flex gap-2" style={{ marginTop: 12 }}>{tlac('nemuzu', 'Nemůžu', X, false)}{tlac('beru', 'Beru', Hand, true)}</div>
            )}
            {d.muzu_vzit && d.moje_odpoved === 'nemuzu' && (
                <div className="flex items-center gap-2" style={{ marginTop: 10 }}>
                    <span className="text-xs flex-1" style={{ color: 'var(--text-muted)' }}>Odpověděl(a) jsi „Nemůžu".</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); akce('beru'); }} className="text-xs font-semibold" style={{ background: 'none', border: 'none', color: SECTION_COLOR, cursor: 'pointer' }}>Nakonec beru</button>
                </div>
            )}
            {d.jsem_kandidat && d.stav === 'ceka_potvrzeni' && (
                <p className="text-xs" style={{ color: 'var(--text-muted)', margin: '10px 0 0' }}>Bereš ji — čeká se na potvrzení vedoucího.</p>
            )}
            {d.smi_potvrdit && (
                <div className="flex gap-2" style={{ marginTop: 12 }}>{tlac('odmitnout-kandidata', 'Odmítnout', X, false)}{tlac('potvrdit', 'Potvrdit', Check, true)}</div>
            )}
            {d.smi_zrusit && !d.smi_potvrdit && (
                <button type="button" onClick={(e) => { e.stopPropagation(); akce('zrusit'); }} className="text-xs" style={{ marginTop: 10, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 0 }}>Zrušit nabídku</button>
            )}
            {chyba && <p className="text-xs" style={{ color: '#F87171', margin: '8px 0 0' }}>{chyba}</p>}
        </div>
    );
}
